/**
 * Horizontal history timeline: syncs year rail with scroll position, prev/next, keyboard.
 */
class YoubouHistoryTimeline extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-youbou-timeline-scroller]');
    this.slides = this.querySelectorAll('[data-youbou-timeline-slide]');
    this.yearButtons = this.querySelectorAll('[data-youbou-timeline-year]');
    this.prevBtn = this.querySelector('[data-youbou-timeline-prev]');
    this.nextBtn = this.querySelector('[data-youbou-timeline-next]');
    this.lightbox = this.querySelector('[data-youbou-timeline-lightbox]');
    this.lightboxImg = this.querySelector('[data-youbou-timeline-lightbox-img]');
    this.scene = this.querySelector('[data-youbou-timeline-scene]');
    this.world = this.querySelector('[data-youbou-timeline-world]');
    this.parallaxLayers = this.querySelectorAll('[data-parallax]');
    this.spacing = 240; // px between stations (matches liquid)
    this.travelTimer = null;

    if (!this.scroller || this.slides.length === 0) return;

    this.#setupScene();
    this.#bindYearClicks();
    this.#bindArrows();
    this.#bindKeyboard();
    this.#bindLightbox();
    this.#bindAddPhoto();
    this.#bindSubmitDialog();
    this.scroller.addEventListener('scroll', () => this.#onScroll(), { passive: true });
    window.addEventListener('resize', () => this.#updateScenePosition(), { passive: true });
    // Ensure carousel and scene start at first slide without animation.
    this.scroller.scrollLeft = 0;
    this.#onScroll();
  }

  #setupScene() {
    if (!this.scene || !this.world) return;
    const totalWidth = (this.slides.length - 1) * this.spacing + this.spacing;
    this.world.style.width = totalWidth + 'px';
    this.sceneReady = false;
  }

  #positionScene(index, animate) {
    if (!this.scene || !this.world) return;
    const halfWidth = this.scene.clientWidth / 2;
    const offset = halfWidth - index * this.spacing;
    if (!animate) {
      this.world.style.transition = 'none';
      this.parallaxLayers.forEach((l) => (l.style.transition = 'none'));
    }
    this.world.style.transform = `translateX(${offset}px)`;
    this.parallaxLayers.forEach((layer) => {
      const speed = parseFloat(layer.dataset.parallax) || 0.5;
      layer.style.transform = `translateX(${offset * speed}px)`;
    });
    if (!animate) {
      requestAnimationFrame(() => {
        this.world.style.transition = '';
        this.parallaxLayers.forEach((l) => (l.style.transition = ''));
      });
    }
  }

  #updateScenePosition() {
    if (!this.scene || !this.world) return;
    const active = this.#activeIndex();
    if (!this.sceneReady) {
      this.sceneReady = true;
      this.#positionScene(active, false);
      return;
    }
    this.#positionScene(active, true);
    this.scene.classList.add('is-traveling');
    if (this.travelTimer) clearTimeout(this.travelTimer);
    this.travelTimer = setTimeout(() => this.scene.classList.remove('is-traveling'), 950);
  }

  #scrollToSlide(index, animate = true) {
    const slide = this.slides[index];
    if (!slide) return;
    const target = slide.offsetLeft - (this.scroller.clientWidth - slide.offsetWidth) / 2;
    if (animate) {
      this.scroller.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      this.scroller.scrollLeft = target;
    }
  }

  #bindYearClicks() {
    this.yearButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(btn.dataset.youbouTimelineYear, 10);
        this.#scrollToSlide(index);
      });
    });
  }

  #bindArrows() {
    this.prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#step(-1);
    });
    this.nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#step(1);
    });
  }

  #bindKeyboard() {
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.#step(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.#step(1);
      } else if (e.key === 'Escape' && this.lightbox?.open) {
        this.lightbox.close();
      }
    });
  }

  #bindLightbox() {
    if (!this.lightbox || !this.lightboxImg) return;
    this.querySelectorAll('[data-youbou-timeline-image-trigger]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const src = btn.dataset.fullSrc;
        const alt = btn.dataset.alt || '';
        if (!src) return;
        this.lightboxImg.src = src;
        this.lightboxImg.alt = alt;
        if (typeof this.lightbox.showModal === 'function') {
          this.lightbox.showModal();
        } else {
          this.lightbox.setAttribute('open', '');
        }
      });
    });
    this.lightbox.addEventListener('click', (e) => {
      // Click outside the image (on the backdrop) closes.
      if (e.target === this.lightbox) this.lightbox.close();
    });
    this.lightbox.querySelector('[data-youbou-timeline-lightbox-close]')?.addEventListener('click', () => {
      this.lightbox.close();
    });
  }

  #bindSubmitDialog() {
    const dialog = document.querySelector('[data-youbou-submit-dialog]');
    if (!dialog) return;
    document.querySelectorAll('[data-youbou-open-submit]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    });
    dialog.querySelector('[data-youbou-close-submit]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
  }

  #bindAddPhoto() {
    this.querySelectorAll('[data-youbou-add-photo]').forEach((wrap) => {
      const input = wrap.querySelector('[data-youbou-add-photo-input]');
      const status = wrap.querySelector('[data-youbou-add-photo-status]');
      if (!input) return;
      input.addEventListener('change', async () => {
        const file = input.files?.[0];
        if (!file) return;
        const handle = wrap.dataset.handle;
        if (!handle) { status.textContent = 'Error: missing handle'; return; }
        status.textContent = 'Uploading…';
        try {
          const form = new FormData();
          form.append('image', file);
          form.append('handle', handle);
          const proxyBase = this.closest('[style]')?.style?.getPropertyValue('--proxy-base')?.trim()
            || '/apps/youbou-history';
          const res = await fetch(proxyBase + '/add-image', { method: 'POST', body: form });
          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || res.statusText);
          }
          status.textContent = 'Photo added! Reload to see it.';
          status.style.color = 'green';
        } catch (e) {
          status.textContent = 'Upload failed: ' + (e.message || e);
          status.style.color = 'red';
        }
      });
    });
  }

  #step(delta) {
    const active = this.#activeIndex();
    const next = Math.min(this.slides.length - 1, Math.max(0, active + delta));
    this.#scrollToSlide(next);
  }

  #activeIndex() {
    const scroller = this.scroller;
    // If at the start, return first slide.
    if (scroller.scrollLeft <= 10) return 0;
    // If at the end, return last slide.
    if (scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 10) return this.slides.length - 1;
    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    this.slides.forEach((slide, i) => {
      const left = slide.offsetLeft;
      const mid = left + slide.offsetWidth / 2;
      const d = Math.abs(mid - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  }

  #onScroll() {
    const active = this.#activeIndex();
    if (active === this.lastActive) {
      // Active didn't change but still need to keep prev/next disabled state correct on first call.
    }
    this.lastActive = active;
    this.yearButtons.forEach((btn, i) => {
      const on = i === active;
      btn.setAttribute('aria-current', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
    });
    this.prevBtn?.toggleAttribute('disabled', active <= 0);
    this.nextBtn?.toggleAttribute('disabled', active >= this.slides.length - 1);
    this.#updateScenePosition();
  }
}

if (!customElements.get('youbou-history-timeline')) {
  customElements.define('youbou-history-timeline', YoubouHistoryTimeline);
}
